import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Badge, statusBadge } from "@/components/Common";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, MagnifyingGlassIcon, UploadIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import BulkImportDialog from "@/components/BulkImportDialog";
import MileageInput from "@/components/MileageInput";
import { sortByUnit } from "@/lib/unitSort";

const empty = {
  unit_number: "",
  vin: "",
  make: "",
  model: "",
  year: new Date().getFullYear(),
  mileage: 0,
  license_plate: "",
  status: "active",
  driver_id: "",
  notes: "",
  unit_system: "",  // "" = auto (company default), "metric", "imperial"
};

export default function Trucks() {
  const nav = useNavigate();
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [sortDir, setSortDir] = useState("asc");

  const load = async () => {
    const [t, d] = await Promise.all([api.get("/trucks"), api.get("/drivers")]);
    setTrucks(t.data);
    setDrivers(d.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const h = (e) => {
      const ent = e?.detail?.entity;
      if (ent === "trucks" || ent === "drivers") load();
    };
    window.addEventListener("rf-data-change", h);
    return () => window.removeEventListener("rf-data-change", h);
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ ...empty, ...t, driver_id: t.driver_id || "", unit_system: t.unit_system || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        year: Number(form.year),
        mileage: Number(form.mileage),
        driver_id: form.driver_id || null,
        // "" means "inherit from company default" — send null to clear override.
        unit_system: form.unit_system || null,
      };
      if (editing) {
        await api.put(`/trucks/${editing.id}`, body);
        toast.success("Truck updated");
      } else {
        await api.post("/trucks", body);
        toast.success("Truck added");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    }
  };

  const driverName = (id) => drivers.find((d) => d.id === id)?.name || "—";

  const filtered = trucks.filter((t) => {
    const q = search.toLowerCase();
    return (
      t.unit_number?.toLowerCase().includes(q) ||
      t.vin?.toLowerCase().includes(q) ||
      t.make?.toLowerCase().includes(q) ||
      t.model?.toLowerCase().includes(q) ||
      t.license_plate?.toLowerCase().includes(q)
    );
  });
  const sorted = sortByUnit(filtered, sortDir);

  return (
    <div data-testid="trucks-page">
      <PageHeader eyebrow="Fleet inventory" title="Trucks" subtitle="All vehicles in your fleet with status, mileage and driver assignment.">
        <Button onClick={() => setBulkOpen(true)} variant="outline" className="rounded-sm" data-testid="bulk-import-trucks-button">
          <UploadIcon size={16} weight="bold" className="mr-2" /> Bulk import
        </Button>
        <Button onClick={openAdd} data-testid="add-truck-button" className="bg-slate-900 hover:bg-slate-800 rounded-sm">
          <PlusIcon size={16} weight="bold" className="mr-2" /> Add Truck
        </Button>
      </PageHeader>

      <BulkImportDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSuccess={load}
        kind="trucks"
        title="trucks"
        columnList="UNIT NUMBER · VIN NUMBER · MAKE · MODEL · LICENSE PLATE"
      />

      <div className="p-8">
        <div className="bg-white border border-slate-300 rounded-sm">
          <div className="p-3 border-b border-slate-200 flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search VIN, unit number, make, model..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="truck-search"
                className="pl-9 rounded-sm border-slate-300"
              />
            </div>
            <span className="font-mono text-xs text-slate-500">{filtered.length} of {trucks.length}</span>
          </div>

          {loading ? (
            <div className="p-8 font-mono text-sm text-slate-500">LOADING...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <div className="font-mono text-xs text-slate-400 mb-2">/// EMPTY GARAGE</div>
              <div className="text-sm text-slate-600 mb-4">No trucks in the fleet yet.</div>
              <Button onClick={openAdd} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="empty-add-truck">
                <PlusIcon size={16} weight="bold" className="mr-2" /> Add your first truck
              </Button>
            </div>
          ) : (
            <table className="fleet-table">
              <thead>
                <tr>
                  <th
                    className="cursor-pointer select-none hover:text-slate-900"
                    onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                    data-testid="trucks-sort-unit"
                    title="Sort by unit number"
                  >
                    Unit # {sortDir === "asc" ? "▲" : "▼"}
                  </th>
                  <th>VIN</th>
                  <th>Make / Model</th>
                  <th>Year</th>
                  <th>Mileage</th>
                  <th>Plate</th>
                  <th>Driver</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((t) => {
                  const sb = statusBadge(t.status);
                  return (
                    <tr key={t.id} className="clickable" onClick={() => nav(`/trucks/${t.id}`)} data-testid={`truck-row-${t.unit_number}`}>
                      <td className="font-mono font-bold text-slate-900">{t.unit_number}</td>
                      <td className="font-mono text-xs text-slate-600">{t.vin}</td>
                      <td className="font-semibold">{t.make} {t.model}</td>
                      <td className="font-mono">{t.year}</td>
                      <td className="font-mono tabular-nums">{Number(t.mileage).toLocaleString()}</td>
                      <td className="font-mono">{t.license_plate || "—"}</td>
                      <td>{driverName(t.driver_id)}</td>
                      <td>
                        <div className="flex items-center gap-1.5">
                          <Badge tone={sb.tone}>{sb.label}</Badge>
                          {t.location_hold && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200" data-testid={`truck-held-${t.unit_number}`} title="Location manually held">
                              📍 Held
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <button
                          onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                          className="text-blue-600 hover:underline text-xs font-semibold"
                          data-testid={`edit-truck-${t.unit_number}`}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="truck-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit truck" : "Add new truck"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <Field label="Unit Number *" required value={form.unit_number} onChange={(v) => setForm({ ...form, unit_number: v })} tid="truck-unit" />
            <Field label="VIN *" required value={form.vin} onChange={(v) => setForm({ ...form, vin: v })} tid="truck-vin" />
            <Field label="Make *" required value={form.make} onChange={(v) => setForm({ ...form, make: v })} tid="truck-make" />
            <Field label="Model *" required value={form.model} onChange={(v) => setForm({ ...form, model: v })} tid="truck-model" />
            <Field label="Year *" required type="number" value={form.year} onChange={(v) => setForm({ ...form, year: v })} tid="truck-year" />
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Mileage</Label>
              <div className="mt-1.5">
                <MileageInput value={form.mileage} onChange={(v) => setForm({ ...form, mileage: v })} data-testid="truck-mileage" />
              </div>
            </div>
            <Field label="License Plate" value={form.license_plate} onChange={(v) => setForm({ ...form, license_plate: v })} tid="truck-plate" />
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="truck-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="maintenance">In Maintenance</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Assigned Driver</Label>
              <Select value={form.driver_id || "none"} onValueChange={(v) => setForm({ ...form, driver_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="truck-driver">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Unassigned —</SelectItem>
                  {drivers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Display units</Label>
              <Select value={form.unit_system || "auto"} onValueChange={(v) => setForm({ ...form, unit_system: v === "auto" ? "" : v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="truck-unit-system">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (use company default)</SelectItem>
                  <SelectItem value="metric">Metric — km / L / km per L</SelectItem>
                  <SelectItem value="imperial">Imperial — mi / gal / MPG</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Overrides the fill-up location. A Canadian truck fueling in the US still shows litres and km on its dashboard.</p>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-sm" data-testid="truck-notes" />
            </div>
            <DialogFooter className="col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" data-testid="truck-save" className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                {editing ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, tid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="mt-1.5 rounded-sm" data-testid={tid} />
    </div>
  );
}
